import { DotLegalHeader, DotLegalTooltip, useGetCurrentBreakpoint } from "@dotlegal/dotlegal-ui-components";
import { Box, Skeleton } from "@mui/material";
import React, { useLayoutEffect, useRef, useState } from "react";

export interface IDotLegalFixedHeightList {
    items: React.ReactNode[];
    isLoading?: boolean;
}

export default function DotLegalFixedHeightList(props: IDotLegalFixedHeightList) {
    const ref = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(props.items.length);
    const [showTooltip, setShowTooltip] = useState(false);
    const breakpoint = useGetCurrentBreakpoint();

    useLayoutEffect(() => {
        if (!ref.current) return;

        const isOverflowing = ref.current.offsetHeight < ref.current.scrollHeight;
        if (isOverflowing && visibleCount > 0) {
            // Remove items one at a time until no overflow
            setVisibleCount((prev) => Math.max(0, prev - 1));
            setShowTooltip(true);
        } else if (!isOverflowing) {
            // Only update tooltip, DON'T add items back automatically
            setShowTooltip(visibleCount < props.items.length);
        }
    }, [visibleCount, props.items.length, breakpoint]);

    useLayoutEffect(() => {
        const handleResize = () => {
            setVisibleCount(props.items.length); // Reset to show all
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [props.items.length]);

    useLayoutEffect(() => {
        setVisibleCount(props.items.length);
    }, [props.items.length]);

    const displayItems = props.items.slice(0, visibleCount);
    const hiddenCount = props.items.length - visibleCount;
    const tooltipText = hiddenCount > 0 ? <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center" }}>{props.items}</Box> : undefined;

    return (
        <React.Fragment>
            {props.isLoading ? (
                <Skeleton variant="text" width={"100%"} />
            ) : (
                <DotLegalTooltip text={tooltipText} disableHoverlistener={!showTooltip}>
                    <Box
                        ref={ref}
                        sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", height: 55, overflow: "hidden", alignItems: "flex-start", justifyContent: "center" }}
                    >
                        {displayItems}

                        {hiddenCount > 0 && (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <DotLegalHeader fontWeight={400} marginBottom={0} headerStyle={"small"} center>
                                    + {hiddenCount}
                                </DotLegalHeader>
                            </Box>
                        )}
                    </Box>
                </DotLegalTooltip>
            )}
        </React.Fragment>
    );
}
